import {
  ReactElement, useEffect, useRef, useState,
} from 'react';
import { notify } from 'lib/utils/honeybadgerUtils';
import { useRouter } from 'next/router';
import { isEqual, omit } from 'lodash';
import { Tune, Add } from '@mui/icons-material';
import {
  ESPaginationInfo, ESServiceMeta,
} from 'server/api/types';
import { useApplicationState } from 'lib/contexts/ApplicationState';
import { CreatorSearchCriteria, CreatorSearchESResponse } from 'server/api/services/creatorSearch/types';
import { userIsAdmin, userIsProOrAdmin } from 'lib/helpers';
import { CreatorsListUser } from 'lib/types/users';
import { humanReadableFromSlug } from 'lib/utils/modelSlugUtils';
import { slugToString } from 'lib/utils/filterUtils';
import usePrevious from 'lib/hooks/usePrevious';
import { AddUsersToList } from 'components/AddUsersToList/AddUsersToList';
import { Pagination } from 'components/Pagination/ESPagination';
import { FAVORITES_LIST_NAME } from 'lib/constants';
import { Collapse } from '@mui/material';
import { ShareButton } from 'components/ShareButton';
import { useModal } from 'mui-modal-provider';
import { AddNewCreator } from 'components/AddNewCreator';
import { FilterCriteria } from './FilterCriteria';
import { FilterCreatorResults } from './FilterCreatorResults';
import { FilterTitle } from './FilterTitle';
import { FeaturedCreators } from './FeaturedCreators';
import { creatorSearchSummary } from './helpers';
import { ListInfo } from './ListInfo';
import { CreatorsSort } from './CreatorsSort';
import { CreatorsCSVExport } from './CreatorsCSVExport';

interface Props {
  title: string;
  richTitle: string | ReactElement;
  serverCreators?: CreatorsListUser[];
  serverMeta?: ESServiceMeta;
  serverCriteria?: CreatorSearchCriteria;
}

type LoadingState = 'idle' | 'needToFetch' | 'loading';

export const Creators = ({
  title, richTitle, serverCreators, serverMeta, serverCriteria,
}: Props) => {
  const router = useRouter();
  const { showModal } = useModal();

  const { api, currentUser, isAuthed } = useApplicationState();
  const [creators, setCreators] = useState<CreatorsListUser[]>(serverCreators || []);
  const [featuredCreators, setFeaturedCreators] = useState<CreatorsListUser[]>([]);
  const [meta, setMeta] = useState<ESServiceMeta | undefined>(serverMeta);
  const [error, setError] = useState<Error | null>(null);
  const [criteria, setCriteria] = useState<CreatorSearchCriteria | undefined>(serverCriteria);
  const prevCriteria = usePrevious(criteria || serverCriteria);
  const [userCanAddToList, setUserCanAddToList] = useState<boolean>(false);
  const [loadingState, setLoadingState] = useState<LoadingState>(serverCreators?.length ? 'idle' : 'needToFetch');
  const [showFilters, setShowFilters] = useState(true);
  const filterRef = useRef(null);
  const [filteredStages, setFilteredStages] = useState<number[]>([]);

  // Function to fetch list details for creators
  const fetchListDetails = async (creatorIds: number[]) => {
    if (!currentUser || !creatorIds.length) {
      return;
    }

    try {
      const listDetails = await api.getCreatorsListDetails(creatorIds);

      // Update creators with new list details
      setCreators((prevCreators) => prevCreators.map((creator) => {
        const details = listDetails[creator.id];
        if (details) {
          return {
            ...creator,
            savedToListsWithStages: details.savedToListsWithStages,
            savedToProjectsWithStages: details.savedToProjectsWithStages,
          };
        }
        return creator;
      }));
    } catch (err) {
      // Silently fail for list details fetching
    }
  };

  useEffect(() => {
    const fetchCreators = async (c: CreatorSearchCriteria) => {
      setLoadingState('loading');
      try {
        const getFeaturedCreators = () => api.featuredCreators(c);

        // When a list is selected, we don't want to show featured creators.
        const emptyResponse = {
          data: [],
          meta: {
            paging: {
              page: 1, size: 0, from: 0, nextFrom: 0, prevFrom: 0, count: 0,
            },
          },
        };

        /**
         * Fetch creators and featured creators at the same time.
         */
        const results = await Promise.allSettled([
          api.searchCreators(c),
          router.query.list ? emptyResponse : getFeaturedCreators(),
        ]);

        const [
          creatorResult, featuredResult,
        ] = results.map(
          (result) => (result.status === 'fulfilled' ? result.value : {
            data: [],
            meta: {
              paging: {
                page: 1, size: 0, from: 0, nextFrom: 0, prevFrom: 0, count: 0,
              },
            },
          } as CreatorSearchESResponse),
        );
        const { data, meta: resultMeta } = creatorResult;
        const { data: featuredData } = featuredResult;
        if (!currentUser?.id) {
          setFeaturedCreators(featuredData);
        } else if (data && data.length > 4 && !router.query.sort && !router.query.page) {
          setFeaturedCreators(featuredData);
        } else {
          setFeaturedCreators([]);
        }
        setCreators([...(creators || []), ...data]);
        setMeta(resultMeta);
        setError(null);
        setLoadingState('idle');

        // Fetch list details for the new creators
        if (data.length > 0) {
          const creatorIds = data.map((creator) => creator.id);
          fetchListDetails(creatorIds);
        }
      } catch (err: any) {
        if (err.name === 'AbortError' || err === 'CANCELLED') {
          return;
        }
        setError(err);
        notify(err, 'Creators/fetchCreators', {
          queryParams: router.query,
        });
        setLoadingState('idle');
      }
    };

    if (loadingState === 'needToFetch' && criteria && isAuthed) {
      fetchCreators(criteria);
    }
  }, [loadingState, criteria, isAuthed]);

  /**
     * Determine whether a user is allowed to add to the currently selected list based
     * on their permissions and what the list is. A user may always add to their Favorites.
     * TODO: Use a response from the API to determine if a user can add to a list.
     */
  async function updateListState(c: CreatorSearchCriteria) {
    if (!c.list) {
      setUserCanAddToList(false);
      return;
    }

    const listName = humanReadableFromSlug(router.query?.list as string);
    if (userIsProOrAdmin(currentUser) || listName === FAVORITES_LIST_NAME) {
      setUserCanAddToList(true);
      return;
    }
    setUserCanAddToList(false);
  }

  useEffect(() => {
    function criteriaHasChanged() {
      // We want to compare current and previous criteria *without* view.
      // If they're the same, no need to re-fetch.
      const prevComparableCriteria = omit(prevCriteria, 'view');
      const comparableCriteria = omit(criteria, 'view');

      if (!isEqual(prevComparableCriteria, comparableCriteria)) {
        if (!isEqual(
          omit(prevComparableCriteria, 'from'),
          omit(comparableCriteria, 'from'),
        )) {
          // When criteria other than the `from` changes, we need to start fresh.
          setCreators([]);
        }
      }

      return !isEqual(prevComparableCriteria, comparableCriteria);
    }

    if (criteria && criteriaHasChanged() && loadingState === 'idle') {
      setLoadingState('needToFetch');
      updateListState(criteria);
    }
  }, [currentUser, criteria]);

  useEffect(() => {
    if (criteria) {
      updateListState(criteria);
    }
  }, [currentUser]);

  // Fetch list details for initial server creators
  useEffect(() => {
    if (serverCreators && serverCreators.length > 0 && currentUser) {
      const creatorIds = serverCreators.map((creator) => creator.id);
      fetchListDetails(creatorIds);
    }
  }, [currentUser]);

  // Used after updating a list to refetch creators and return to 'page' 1
  const refetchCreators = () => {
    setCreators([]);
    setCriteria({ list: criteria?.list });
    router.push({
      query: {
        ...router.query,
        from: [],
      },
    });
    setLoadingState('needToFetch');
  };

  /** Display only when on the first page, with no custom sort,
   *  and only when there are more than 4 results on the page */
  const displayFeaturedCreators = !!(
    featuredCreators.length
    && creators
    && creators.length > 4
    && !router.query.sort
    && !router.query.list
    && (meta?.paging?.from || 0) === 0)
    || (!currentUser?.hasPaidTeam && featuredCreators.length > 0);

  /** If the user is looking at a list and is allowed to add to it */
  const showAddToListButton = !!criteria?.list && (userCanAddToList || userIsAdmin(currentUser));

  /** If the user is looking at a list that has more than 3 creators
 *  and is NOT allowed to add to it */
  const showSignUpForProButton = !!criteria?.list
    && !userCanAddToList
    && !!meta?.paging?.count
    && meta?.paging?.count > 3
    && creators?.length > meta?.paging?.count;

  const flexEmoji = String.fromCodePoint(0x1F4AA);

  const publicId = (listSlug: string) => slugToString(listSlug);
  const isLoading = loadingState === 'loading' || loadingState === 'needToFetch';

  const handleModal = () => {
    const modal = showModal(AddNewCreator, {
      onClose: () => {
        modal.destroy();
      },
      onSave: () => {
        modal.destroy();
      },
    });
  };

  useEffect(() => {
    if (criteria?.creatorStage) {
      console.log(criteria);
      setFilteredStages(criteria.creatorStage);
    } else {
      setFilteredStages([]);
    }
  }, [criteria?.creatorStage]);

  return (
    <div className="p-3 sm:p-6 max-w-screen-2xl m-auto">
      <div ref={filterRef}>
        <Collapse in={showFilters}>
          <div className="mb-2">
            <FilterCriteria
              creatorsFound={!!creators?.length}
              title={title}
              onChange={setCriteria}
            />
          </div>
        </Collapse>
      </div>
      <div className="flex flex-col-reverse md:flex-row items-start justify-between gap-1 mb-2">
        <FilterTitle
          count={meta?.paging?.count}
          richTitle={richTitle || title}
          isLoading={isLoading}
        />
        <div className="flex flex-wrap items-end w-full md:w-auto mb-2 md:mb-0 gap-2">
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className="btn-primary-outlined-xs"
          >
            <div className="flex items-center uppercase">
              <Tune fontSize="small" className="mr-1" />
              Filters
            </div>
          </button>
          <div className="flex items-center">
            <ShareButton text={title} />
          </div>
          <div>
            <CreatorsSort criteria={criteria} />
          </div>
          {userIsAdmin(currentUser) && (
            <>
              <CreatorsCSVExport
                creators={creators}
              />
              <button
                type="button"
                onClick={handleModal}
                className="btn-primary-outlined-xs"
              >
                <div className="flex items-center uppercase">
                  <Add fontSize="small" className="mr-1" />
                  Creator
                </div>
              </button>
            </>
          )}
        </div>
      </div>

      {(!isLoading && criteria?.list
        && (humanReadableFromSlug(router.query?.list as string) !== FAVORITES_LIST_NAME)) && (
        <>
          <ListInfo publicListId={criteria.list} filteredStages={filteredStages} />
        </>
      )}
      {displayFeaturedCreators
        && (
          <FeaturedCreators
            creators={featuredCreators}
            onListsUpdated={() => {
            // Refresh list details for all current creators
              const creatorIds = creators.map((creator) => creator.id);
              fetchListDetails(creatorIds);
            }}
          />
        )}
      <FilterCreatorResults
        creators={creators}
        isLoading={isLoading}
        error={error}
        publicListId={criteria?.list}
        count={meta?.paging?.count}
        onListsUpdated={() => {
          // Refresh list details for all current creators
          const creatorIds = creators.map((creator) => creator.id);
          fetchListDetails(creatorIds);
        }}
        filteredStages={filteredStages}
      />
      {(showAddToListButton && !!router.query.list) && (
        <AddUsersToList
          publicId={publicId(router.query.list as string)}
          onCreatorsAdd={refetchCreators}
        />
      )}
      {showSignUpForProButton && (
        <div className="text-center pt-4">
          <div className="text-sm mb-2">
            This is just the first three creators from this list.
            Sign up for PRO to see
            {' '}
            <span className="font-semibold">
              {meta?.paging?.count - 3}
              {' '}
              more
            </span>
            .
          </div>
          <button className="btn-primary" type="button" onClick={() => router.push({ pathname: '/plans', query: router.pathname })}>
            Let&apos;s go PRO&nbsp;
            {flexEmoji}

          </button>
        </div>
      )}

      {!!meta?.paging && currentUser?.id && (
        <Pagination
          paging={meta?.paging as ESPaginationInfo}
          isLoading={isLoading}
          hideNoPaginationBox
          disableLoadMore={!currentUser?.hasPaidTeam && !criteria?.list}
        />
      )}
      {(!isLoading && !router.query.userType)
        && creatorSearchSummary(router.query, creators, meta?.paging?.count)}
    </div>
  );
};
